import React from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { cn } from "../../../lib/cn";

/**
 * Table — ui-v2.5. Tabla de datos. Especificación: catálogo · sección Table.
 * Cabecera hundida, celdas, hover de fila, fila destacada, ordenación (3 estados), selección.
 * El radio del contenedor se consigue con overflow:hidden, no en la tabla.
 * Acepta un pie (Pagination) DENTRO del contenedor vía la prop `footer`.
 *
 * columns: [{ key, header, sortable?, render?(row), width?, className? }]
 * rows: array de objetos (id por `rowKey`, "id" por defecto).
 * <Table columns rows rowKey? onSort sortBy sortDir selectable selected onSelect rowHighlight footer emptyState />
 *  - onSort(key), sortBy, sortDir="asc|desc" · selected: array de ids · onSelect(ids)
 *  - rowHighlight(row) => boolean · footer/emptyState: nodos.
 */

export function Table({
  columns = [],
  rows = [],
  rowKey = "id",
  onSort,
  sortBy,
  sortDir,
  selectable = false,
  selected = [],
  onSelect,
  rowHighlight,
  footer,
  emptyState,
  className,
}) {
  const ids = rows.map((r) => r[rowKey]);
  const allSelected = selectable && rows.length > 0 && ids.every((id) => selected.includes(id));
  const toggleAll = () => onSelect?.(allSelected ? [] : ids);
  const toggleOne = (id) => onSelect?.(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div className={cn("overflow-hidden rounded-v25-lg border border-v25-line bg-white", className)}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-v25-surface-sunken">
              {selectable && (
                <th className="w-9 px-3 py-2.5">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Seleccionar todo" className="h-4 w-4 align-middle" />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={col.width ? { width: col.width } : undefined}
                  className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-v25-ink-500"
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort?.(col.key)}
                      className={cn("inline-flex items-center gap-1 focus-visible:outline-none focus-visible:shadow-v25-focus", sortBy === col.key ? "text-v25-brand-800" : "text-v25-ink-500 hover:text-v25-ink-700")}
                    >
                      {col.header}
                      {sortBy === col.key ? (
                        sortDir === "desc" ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />
                      ) : (
                        <ChevronsUpDown className="h-3 w-3" />
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const id = row[rowKey];
              const highlighted = typeof rowHighlight === "function" ? rowHighlight(row) : false;
              const isSel = selected.includes(id);
              return (
                <tr
                  key={id}
                  className={cn(
                    "transition-colors",
                    highlighted || isSel ? "bg-v25-prefill-bg" : "hover:bg-v25-surface-sunken"
                  )}
                >
                  {selectable && (
                    <td className={cn("px-3 py-3", i > 0 && "border-t border-v25-surface-raised")}>
                      <input type="checkbox" checked={isSel} onChange={() => toggleOne(id)} aria-label="Seleccionar fila" className="h-4 w-4 align-middle" />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key} className={cn("px-3 py-3 text-v25-ink-700", i > 0 && "border-t border-v25-surface-raised", col.className)}>
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && emptyState}
      {footer}
    </div>
  );
}
